"use client";
import * as React from "react";
import {
  Typography,
  Button,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import { Upload, FileText, Trash2, BookOpen } from "lucide-react";
import { WorkspaceCard } from "../Common/WorkspaceCard";
import { ConfirmDialog } from "../Common/ConfirmDialog";
import { usePortalScope } from "@/analytics/PortalScope";
import { Row, Col } from "../Common/Row";
import { DocumentStatusChip } from "../Knowledge/DocumentStatusChip";
import { useAuthedApi } from "../../api/useAuthedApi";
import type { DocumentRow } from "../../api/documentsApi";
import { RestError } from "../../api/restClient";

interface Props {
  projectId: string;
  documents: DocumentRow[];
  /** Called after an upload or delete so the parent re-fetches the list. */
  onChanged: () => void;
}

function formatBytes(n: number | null | undefined): string {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function ProjectKnowledgeList({ projectId, documents, onChanged }: Props) {
  const { documents: docsApi } = useAuthedApi();
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = React.useState<DocumentRow | null>(null);
  const deleteDialogScope = usePortalScope("files-delete_dialog")["data-ph-scope"];
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  // Poll while anything is still processing so the badge settles without a
  // manual reload (mirrors the Knowledge page).
  React.useEffect(() => {
    const anyProcessing = documents.some((d) => d.status === "processing");
    if (!anyProcessing) return;
    const id = setInterval(onChanged, 4000);
    return () => clearInterval(id);
  }, [documents, onChanged]);

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!docsApi) return;
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      // Project files are searchable knowledge scoped to this project: the
      // assistant only retrieves them when chatting inside the project.
      await docsApi.upload({ file, intent: "knowledge", project_id: projectId });
      onChanged();
    } catch (err) {
      setError(
        err instanceof RestError ? err.detail : err instanceof Error ? err.message : String(err),
      );
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    if (!docsApi || !pendingDelete) return;
    setError(null);
    try {
      await docsApi.remove(pendingDelete.id);
      setPendingDelete(null);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      throw err; // surfaced inline by ConfirmDialog
    }
  };

  return (
    <>
    <WorkspaceCard interactive={false} data-ph-scope="files">
      <Row align="center" justify="space-between" gap={1}>
        <Row align="center" gap={1}>
          <BookOpen size={16} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Files ({documents.length})
          </Typography>
        </Row>
        <Button
          size="small"
          variant="contained"
          data-ph="upload"
          startIcon={<Upload size={14} />}
          onClick={() => fileRef.current?.click()}
          disabled={uploading || !docsApi}
        >
          {uploading ? "Uploading…" : "Add file"}
        </Button>
        <input ref={fileRef} type="file" hidden onChange={onUpload} aria-label="upload project file" />
      </Row>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        Files added here become searchable knowledge Artie can draw on in this project’s chats.
      </Typography>

      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

      {documents.length > 0 && (
        <Col gap={0.75} sx={{ mt: 1.5 }}>
          {documents.map((d) => (
            <Row key={d.id} align="center" gap={1.5} sx={{ minWidth: 0 }}>
              <FileText size={18} aria-hidden style={{ flexShrink: 0 }} />
              <Col sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {d.title || d.filename}
                </Typography>
                <Row align="center" gap={1}>
                  <DocumentStatusChip status={d.status} />
                  <Typography variant="caption" color="text.secondary">
                    {formatBytes(d.byte_size)}
                    {d.chunk_count ? ` · ${d.chunk_count} chunks` : ""}
                  </Typography>
                </Row>
              </Col>
              <Tooltip title="Remove from project">
                <IconButton size="small" data-ph="delete" onClick={() => setPendingDelete(d)} aria-label={`remove ${d.filename}`}>
                  <Trash2 size={14} />
                </IconButton>
              </Tooltip>
            </Row>
          ))}
        </Col>
      )}
    </WorkspaceCard>
    <ConfirmDialog
      open={!!pendingDelete}
      title="Remove from this project?"
      body={<><strong>{pendingDelete?.filename}</strong> will be removed from this project.</>}
      confirmLabel="Remove"
      destructive
      onConfirm={confirmDelete}
      onClose={() => setPendingDelete(null)}
      scope={deleteDialogScope}
    />
    </>
  );
}
